/**
 * DeepDiveOptional — optionele post-demo verdieping.
 *
 * Bevat sinds Feb 2026 (commercial cut):
 *   - Woonsituatie + huishoudgrootte + postcode (4 cijfers) — verplaatst
 *     uit de Quick Scan zodat die korter werd (3 vragen vóór demo).
 *   - De originele technische vragen (contract, control_preference,
 *     installation_preference, monitoring_interest).
 *   - Optioneel email om het rapport te ontvangen.
 *
 * Niets is verplicht. Submit met lege velden is OK — backend slaat
 * gewoon null op.
 */
import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import SingleSelectCards from './SingleSelectCards';

export default function DeepDiveOptional({ loading, onSubmit, onCancel }) {
  const [living, setLiving] = useState('');
  const [householdSize, setHouseholdSize] = useState('');
  const [postcode4, setPostcode4] = useState('');
  const [contract, setContract] = useState('');
  const [control, setControl] = useState('');
  const [installation, setInstallation] = useState('');
  const [monitoring, setMonitoring] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = () => {
    onSubmit({
      living,
      household_size: householdSize ? Number(householdSize) : null,
      postcode4: (postcode4 || '').replace(/\D/g, '').slice(0, 4) || null,
      contract_type: contract,
      control_preference: control,
      installation_preference: installation,
      monitoring_interest: monitoring,
      email: (email || '').trim(),
    });
  };

  return (
    <section
      className="space-y-6 rounded-2xl border border-border bg-white p-5 sm:p-7 shadow-sm"
      data-testid="deepdive-root"
    >
      <header>
        <h2
          className="text-2xl sm:text-3xl font-medium text-primary leading-tight"
          style={{ fontFamily: 'Outfit' }}
        >
          Optioneel — maak uw plan concreter
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          Een paar extra vragen + (optioneel) uw e-mail om het rapport te ontvangen.
          Niets is verplicht. U kunt dit overslaan en direct met Energy Control starten.
        </p>
      </header>

      {/* ----- Woonsituatie ----- */}
      <SingleSelectCards
        label="Wat is uw woonsituatie?"
        value={living}
        options={[
          { value: 'own_house', label: 'Koopwoning — huis' },
          { value: 'own_apartment', label: 'Koopwoning — appartement' },
          { value: 'rent_house', label: 'Huurwoning — huis' },
          { value: 'rent_apartment', label: 'Huurwoning — appartement' },
          { value: 'other', label: 'Anders / weet ik niet' },
        ]}
        onChange={setLiving}
        testIdPrefix="dd-living"
        columns={2}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="dd-household-size" className="text-[14px] font-medium block mb-2">
            Hoeveel personen wonen er? <span className="text-muted-foreground font-normal">(optioneel)</span>
          </label>
          <input
            id="dd-household-size"
            type="number"
            inputMode="numeric"
            min={1}
            max={12}
            placeholder="bijv. 3"
            value={householdSize}
            onChange={(e) => setHouseholdSize(e.target.value)}
            className="w-32 px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            data-testid="dd-household-size-input"
          />
        </div>
        <div>
          <label htmlFor="dd-postcode" className="text-[14px] font-medium block mb-2">
            Eerste 4 cijfers postcode <span className="text-muted-foreground font-normal">(optioneel, voor regio-indicatie)</span>
          </label>
          <input
            id="dd-postcode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="bijv. 1012"
            value={postcode4}
            onChange={(e) => setPostcode4(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-32 px-3 py-2 rounded-md border border-border bg-background text-sm tracking-widest focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            data-testid="dd-postcode-input"
          />
        </div>
      </div>

      {/* ----- Technische verdieping ----- */}
      <SingleSelectCards
        label="Wat voor energiecontract heeft u?"
        value={contract}
        options={[
          { value: 'fixed', label: 'Vast' },
          { value: 'variable', label: 'Variabel' },
          { value: 'dynamic', label: 'Dynamisch (uurprijs)' },
          { value: 'unknown', label: 'Weet ik niet' },
        ]}
        onChange={setContract}
        testIdPrefix="dd-contract"
        columns={2}
      />

      <SingleSelectCards
        label="Hoeveel sturing wilt u dat Powersure (in de toekomst) doet?"
        hint="U kunt dit later altijd aanpassen. Niets gebeurt zonder uw expliciete toestemming."
        value={control}
        options={[
          { value: 'advisory_only', label: 'Alleen advies — ik beslis zelf' },
          { value: 'smart_plug_only', label: 'Alleen via slimme stekkers' },
          { value: 'auto_with_consent', label: 'Auto-sturing als ik daar OK voor geef' },
        ]}
        onChange={setControl}
        testIdPrefix="dd-control"
        columns={1}
      />

      <SingleSelectCards
        label="Hoe pakt u nieuwe installaties bij voorkeur aan?"
        value={installation}
        options={[
          { value: 'diy', label: 'Doe-het-zelf waar het mag' },
          { value: 'installer', label: 'Liever een vakman / installateur' },
          { value: 'unsure', label: 'Weet ik nog niet' },
        ]}
        onChange={setInstallation}
        testIdPrefix="dd-installation"
        columns={1}
      />

      <SingleSelectCards
        label="Wilt u dat Powersure uw verbruik blijft monitoren?"
        value={monitoring}
        options={[
          { value: 'yes', label: 'Ja graag' },
          { value: 'maybe', label: 'Misschien — vertel me meer' },
          { value: 'no', label: 'Nee, voor nu niet' },
        ]}
        onChange={setMonitoring}
        testIdPrefix="dd-monitoring"
        columns={3}
      />

      <div>
        <label
          htmlFor="dd-email"
          className="text-[14px] font-medium block mb-2"
        >
          Uw e-mailadres <span className="text-muted-foreground font-normal">(optioneel — om het rapport te ontvangen)</span>
        </label>
        <input
          id="dd-email"
          type="email"
          autoComplete="email"
          placeholder="naam@voorbeeld.nl"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full sm:w-80 px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          data-testid="dd-email-input"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md border border-border text-foreground hover:bg-muted"
          data-testid="dd-back-btn"
        >
          Terug naar demo
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="btn-primary flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-6 py-2 rounded-md disabled:opacity-50"
          data-testid="dd-submit-btn"
        >
          {loading ? 'Opslaan…' : 'Stuur mijn plan'} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
